<template>
  <div class="query-component">
    <!-- Toggle Button -->
    <button v-if="!isCompact"
      @click="isOpen = !isOpen"
      class="toggle-btn"
      :class="{ 'active': isOpen }"
    >
      <q-icon name="help_outline" size="20px" />
    </button>

    <!-- Panel - koristi Quasar klase -->
    <q-card v-if="isOpen" class="map-panel">
      <!-- Header -->
      <div class="panel-header panel-header--fixed q-px-md q-pt-md q-pb-sm">
        <div class="text-subtitle2 section-title">
          {{ showingResults ? $t('Results') : $t('Query') }}
        </div>
        <q-btn 
          @click="isOpen = false" 
          icon="close" 
          flat round dense size="sm"
          :color="$q.dark.isActive ? 'grey-5' : 'grey-6'"
        />
      </div>

      <div class="q-px-md">
        <q-separator />
      </div>

      <div class="panel-content q-px-md q-pb-md">
        <!-- QUERY FORM -->
        <!-- <div v-if="!showingResults" class="q-gutter-sm q-mt-md"> -->
        <div v-if="!showingResults" :class="[$q.screen.gt.sm ? 'q-gutter-md' : 'q-gutter-sm', 'q-mt-md']">
        
          <!-- Mode Selection -->
          <div>
            <div class="text-subtitle2 section-title q-mb-sm">{{ $t('Query Type') }}</div>
            <q-option-group
              v-model="queryMode"
              :options="modeOptions"
              :color="$q.dark.isActive ? 'secondary' : 'primary'"
              inline
              size="xs"
              :disable="isDrawing"
            />
          </div>

          <!-- Point Mode Status -->
          <div v-if="queryMode === 'point'" class="status-section q-pa-sm">
            <div v-if="selectedPoint" class="status-info success">
              <q-icon name="place" class="q-mr-sm" />
              {{ $t('Point') }}: {{ selectedPoint[0].toFixed(4) }}, {{ selectedPoint[1].toFixed(4) }}
            </div>
            <div v-else class="status-info">
              <q-icon name="touch_app" class="q-mr-sm" />
              {{ $t('Click on map to select location') }}
            </div>
          </div>

          <!-- Field Mode Status -->
          <div v-if="queryMode === 'field'" class="status-section q-pa-sm">
            <div v-if="selectedField" class="status-info success">
              <q-icon name="check_circle" class="q-mr-sm" />
              {{ $t('Field selected') }}: {{ selectedField.name || selectedField.key || $t('Unknown Field') }}
            </div>
            <div v-else class="status-info">
              <q-icon name="touch_app" class="q-mr-sm" />
              {{ $t('Click on a field to select it') }}
            </div>
          </div>

          <q-separator />

          <!-- Question Selection -->
          <div>
            <div class="text-subtitle2 section-title q-mb-sm">{{ $t('Question') }}</div>
            <q-select
              v-model="selectedQuestion"
              :options="questionOptions"
              option-value="value"
              option-label="label"
              emit-value
              map-options
              outlined
              dense
              behavior="menu"
              :loading="loadingQuestions"
              :color="$q.dark.isActive ? 'secondary' : 'primary'"
            >
              <template #option="{ opt, itemProps }">
                <q-item v-bind="itemProps">
                  <q-item-section>{{ $t(opt.label) }}</q-item-section>
                </q-item>
              </template>
              <template #selected-item="{ opt }">
                {{ $t(opt.label) }}
              </template>
            </q-select>
          </div>

          <!-- Selected Question Display -->
          <div class="question-label q-pa-sm">
            {{ $t(finalQuestion) }}
          </div>

          <q-separator />

         <!-- Submit Button -->
         <div>
          <q-btn
            @click="submitQuery"
            :disable="!canSubmit"
            :loading="loading"
            :color="$q.dark.isActive ? 'secondary' : 'primary'"
            :label="$t('Submit Query')"
            class="full-width"
            unelevated
          />
        </div>
      </div>

      <!-- RESULTS -->
      <div v-else class="q-mt-md">
        <q-btn
          @click="backToForm"
          icon="arrow_back"
          :label="$t('New Query')"
          flat
          :color="$q.dark.isActive ? 'secondary' : 'primary'"
          class="q-mb-md"
          size="sm"
        />
        <QueryResults :result="result"/>
      </div>

      <!-- Error -->
      <div v-if="error" class="error-section q-mt-md q-pa-sm">
        <div class="error-message">
          <q-icon name="error" class="q-mr-sm" />
          {{ error }}
          <q-btn 
            @click="error = null" 
            flat round dense
            icon="close"
            size="sm"
          />
        </div>
      </div>
      </div>
    </q-card>

     <!-- Compact Results -->
   <!-- Compact Results -->
<div v-if="isCompact && !isOpen && (loading || result)" class="compact-results-position">
  <div class="compact-results-wrapper">
    <LoadingOverlay 
      :show="loading"
      :text="$t('Loading')"
      size="sm"
      inline
    />
    
    <QueryResults 
      v-if="result && !loading"
      :result="result" 
      :compact="true"
      :title="selectedQuestionLabel"
      @expand="handleCompactExpand"
    />
  </div>
</div>
  </div>
</template>

<script>
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import { Style, Circle, Fill, Stroke } from 'ol/style'
import { toLonLat } from 'ol/proj'
import Draw from 'ol/interaction/Draw'
import GeoJSON from 'ol/format/GeoJSON'
import QueryResults from './query-result.vue'
import LoadingOverlay from '../ui/loading-overlay.vue'

export default {
  name: 'HybridQueryComponent',
  components: {
    QueryResults,
    LoadingOverlay
  },
  props: {
    map: Object,
    projection: Object,
    isCompact: Boolean,
    cachedResult: Object,
    activePanel: { type: String, default: null }
  },

  emits: ['query-submitted', 'panel-opened'],
  data() {
    return {
      selectedField: null,
      lastSelectedFeature: null,
      isOpen: false,
      queryMode: 'point',
      selectedPoint: null,
      drawnPolygon: null,
      isDrawing: false,
      selectedQuestion: "What is my land cover over at the moment?",
      customQuestion: "",
      loading: false,
      result: null,
      error: null,
      showingResults: false,
      
      modeOptions: [
        { label: this.$t('Point'), value: 'point' },
        { label: this.$t('Field'), value: 'field' }
      ],
      
      questionOptions: [],
      
      vectorSource: null,
      vectorLayer: null,
      clickHandler: null,
      drawInteraction: null,
      loadingQuestions: false,
    }
  },

  created() {
    this.fetchQueries();
  },

  computed: {
    selectedQuestionLabel() {
      const question = this.questionOptions.find(q => q.value === this.selectedQuestion)
      return question?.label || 'bla'
    },
    canSubmit() {
      const hasQuestion = this.customQuestion.trim() || this.selectedQuestion
      const hasGeometry = (this.queryMode === 'point' && this.selectedPoint) ||
                         (this.queryMode === 'polygon' && this.drawnPolygon) ||
                         (this.queryMode === 'field' && this.selectedField)
      return hasQuestion && hasGeometry && !this.loading && !this.isDrawing
    },

    finalQuestion() {
      return this.customQuestion.trim() || this.selectedQuestion
    }
  },

  watch: {
    activePanel(val) {
      if (val && val !== 'query') {
        this.isOpen = false
      }
    },
    cachedResult: {
      handler(newResult) {
        if (newResult && !this.result) {
          this.result = newResult
          this.showingResults = true
          console.log('📦 Loaded cached result')
        }
      },
      immediate: true
    },
    isOpen(newVal) {
      if (newVal) {
        this.$emit('panel-opened')
        this.setupMap()
      } else {
        this.cleanup()
      }
    },

    queryMode() {
      this.cleanup()
      this.selectedPoint = null
      this.drawnPolygon = null

      if (this.lastSelectedFeature) {
        this.lastSelectedFeature.setStyle(null);
        this.lastSelectedFeature = null;
      }

      if (this.isOpen) {
        this.setupMap()
      }
    }
  },

  beforeUnmount() {
    this.cleanup()
  },

  methods: {
    showCachedResult(result) {
      this.result = result
      this.showingResults = true
      if (!this.isCompact) {
        this.isOpen = true
      }
    },
    async showHomeQuery(homeData, query) {
      try {
        
        // Postavite odabrano polje za query
        this.queryMode = 'point';
        this.selectedPoint = homeData.coordinate;
        this.selectedQuestion = this.questionOptions.find(q => q.label === query.title)?.value;
        
        // Dodajte marker na mapu
        if (this.vectorSource) {
          this.vectorSource.clear();
          const coordinate = transform(
            homeData.coordinate,
            'EPSG:4326',
            this.projection
          );
          const marker = new Feature({
            geometry: new Point(coordinate)
          });
          this.vectorSource.addFeature(marker);
        }
        
        // Submitajte query automatski
        await this.submitQuery();

        if(!this.isCompact && !this.isOpen){
            this.isOpen = true;
        }
        
        
      } catch (err) {
        console.error('Error showing home query:', err);
        this.error = `Pogreška: ${err.message}`;
      }
    },
    backToForm() {
      this.showingResults = false
      this.result = null
    },

    // Metode za compact results
    getCurrentValue() {
      if (!this.result?.data?.data?.length) return null;
      return this.result.data.data[this.result.data.data.length - 1].value;
    },

    getCurrentValueLabel() {
      const value = this.getCurrentValue();
      if (value === null) return 'No data';
      return this.result.data.collection.continuous 
        ? value.toFixed(2) 
        : `Class ${value}`;
    },

    getMiniChartPoints() {
      if (!this.result?.data?.data) return '';
      
      const data = this.result.data.data;
      const values = data.map(item => item.mean || item.value || 0);
      const minValue = Math.min(...values) * 0.95;
      const maxValue = Math.max(...values) * 1.05;
      const valueRange = maxValue - minValue;
      
      return data.map((item, index) => {
        const x = index * (100 / (data.length - 1));
        const value = item.mean || item.value || 0;
        const y = 35 - ((value - minValue) / valueRange) * 30;
        return `${x},${y}`;
      }).join(' ');
    },

    getTrendIcon(trend) {
      if (trend > 0.05) return 'trending_up'
      if (trend < -0.05) return 'trending_down'
      return 'trending_flat'
    },

    getTrendClass(trend) {
      if (trend > 0.05) return 'trend-positive'
      if (trend < -0.05) return 'trend-negative'
      return 'trend-neutral'
    },

    getLandCoverColor(classValue) {
      const colors = {
        1: '#1e40af', 2: '#059669', 3: '#d97706', 4: '#dc2626',
        5: '#7c3aed', 6: '#0891b2', 7: '#4338ca', 8: '#be123c'
      }
      return colors[classValue] || '#6b7280'
    },

    async fetchQueries() {
      try {
        this.loadingQuestions = true;
        const response = await this.get('User/GetFarmQueries');
        
        this.questionOptions = response.map(item => ({
          label: item.title,
          value: item.query,
          rawQuery: item.query
        }));
        
        if (this.questionOptions.length > 0) {
          this.selectedQuestion = this.questionOptions[0].value;
        }
      } catch (error) {
        console.error('Failed to fetch queries:', error);
        this.error = 'Failed to load query options. Please try again.';
      } finally {
        this.loadingQuestions = false;
      }
    },

    setupMap() {
      if (!this.map) return
      this.createVectorLayer()
      this.setupMapClicks()
    },

    setupMapClicks() {
      if (this.clickHandler) {
        this.map.un('singleclick', this.clickHandler)
      }
      
      this.clickHandler = (event) => {
        if (this.queryMode === 'point') {
          this.handlePointClick(event);
        } else if (this.queryMode === 'field') {
          this.handleFieldClick(event);
        }
      }

      this.map.on('singleclick', this.clickHandler)
    },

    createVectorLayer() {
      if (this.vectorLayer) {
        this.map.removeLayer(this.vectorLayer)
      }

      this.vectorSource = new VectorSource()
      this.vectorLayer = new VectorLayer({
        source: this.vectorSource,
        style: new Style({
          image: new Circle({
            radius: 8,
            fill: new Fill({ color: '#1976d2' }),
            stroke: new Stroke({ color: '#ffffff', width: 2 })
          }),
          stroke: new Stroke({
            color: '#1976d2',
            width: 2
          }),
          fill: new Fill({
            color: 'rgba(25, 118, 210, 0.1)'
          })
        }),
        zIndex: 1000
      })

      this.map.addLayer(this.vectorLayer)
    },

    handlePointClick(event) {
      if (this.isDrawing) return
        
      const coordinate = event.coordinate
      const lonLat = toLonLat(coordinate, this.projection)
        
      this.selectedPoint = lonLat
      this.addPointMarker(coordinate)
    },

    handleFieldClick(event) {
      if (this.lastSelectedFeature) {
        this.lastSelectedFeature.setStyle(null);
      }
      
      const features = [];
      this.map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
        if (layer && layer.get('name') === 'fieldLayer') {
          features.push(feature);
        }
      });
      
      if (features.length > 0) {
        const selectedFeature = features[0];
        const properties = selectedFeature.getProperties();
        
        const { geometry, ...fieldData } = properties;
        this.selectedField = fieldData;
        
        selectedFeature.setStyle(new Style({
          fill: new Fill({ color: 'rgba(255, 255, 0, 0.3)' }),
          stroke: new Stroke({ color: '#FF5722', width: 3 })
        }));
        
        this.lastSelectedFeature = selectedFeature;
      } else {
        this.selectedField = null;
      }
    },

    addPointMarker(coordinate) {
      this.vectorSource.clear()
      
      const marker = new Feature({
        geometry: new Point(coordinate)
      })
      
      this.vectorSource.addFeature(marker)
    },

    async submitQuery() {
      if (!this.canSubmit) return

      this.loading = true
      this.error = null

      try {
        let geometry, geometryType
        
        if (this.queryMode === 'point') {
          geometry = { type: 'Point', coordinates: this.selectedPoint }
          geometryType = 'point'
        } else if(this.queryMode === 'polygon'){
          geometry = this.drawnPolygon
          geometryType = 'polygon'
        } else if(this.queryMode === 'field'){
          if (this.lastSelectedFeature && this.lastSelectedFeature.getGeometry()) {
            const olGeometry = this.lastSelectedFeature.getGeometry()
            const coords = olGeometry.getCoordinates()
            
            const transformedCoords = coords.map(ring => 
              ring.map(coord => toLonLat(coord, this.projection))
            )
            
            geometry = {
              type: 'Polygon',
              coordinates: transformedCoords
            }
            geometryType = 'polygon'
          } else {
            throw new Error('Field geometry is not available')
          }
        }

        // API call - šalji samo što API očekuje
        const apiResponse = await this.post("Chat/QuestionQuery", {
          prompt: this.finalQuestion,
          geometry: geometry,
          summarize: false
        })

        // Dodaj geometryType za frontend
        this.result = {
          ...apiResponse,
          geometryType: geometryType 
        }

        this.showingResults = true
        this.$emit('query-submitted', this.result) 
      } catch (err) {
        this.error = `Query failed: ${err.message}`
      } finally {
        this.loading = false
      }
    },

    cleanup() {
      if (this.clickHandler && this.map) {
        this.map.un('singleclick', this.clickHandler)
        this.clickHandler = null
      }

      if (this.drawInteraction && this.map) {
        this.map.removeInteraction(this.drawInteraction)
        this.drawInteraction = null
      }

      if (this.vectorLayer && this.map) {
        this.map.removeLayer(this.vectorLayer)
        this.vectorLayer = null
        this.vectorSource = null
      }

      if (this.lastSelectedFeature) {
        this.lastSelectedFeature.setStyle(null);
        this.lastSelectedFeature = null;
      }

      this.isDrawing = false
    }
  }
}
</script>

<style lang="scss" scoped>
.query-component {
  position: absolute;
  top: var(--map-ui-top);
  left: var(--map-ui-left);
  z-index: var(--z-map-ui);
}

// .panels-container .query-component {
//   @media (min-width: 48rem) {
//     position: static;
//   }
// }

.status-section {
  background: $grey-2; /* #f8f9fa; */
  border-radius: var(--radius-sm);

  body.body--dark & {
    background: $grey-9;
  }
}

.status-info {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  color: $grey-7;

  body.body--dark & { color: $grey-5; }

  &.success {
    color: $positive;
  }
}

.error-section {
  background: rgba($negative, 0.1); /* #f8d7da; */
  border: 1px solid rgba($negative, 0.3); /* #f5c6cb; */
  border-radius: var(--radius-sm);

  body.body--dark & {
    background: rgba($negative, 0.2);
    border-color: rgba($negative, 0.4);
  }
}

.error-message {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: $negative;
  font-size: 0.875rem;
}

.question-label {
  background-color: $grey-2; /* #f5f5f5; */
  border-radius: var(--radius-sm);
  border: 1px solid $grey-4; /* #e0e0e0; */
  font-size: 0.875rem;
  color: $grey-8; /* #333; */
  min-height: 1.5rem;

  body.body--dark & {
    background-color: $grey-9;
    border-color: $grey-7;
    color: $grey-4;
  }
}

@media (max-width: 48rem), (max-height: 48rem)  { /* 768px */
  .query-component {
     top: var(--map-ui-top-mobile);
     left: var(--map-ui-left-mobile);
  }

  .query-panel {
    width: calc(100vw - 6rem);
    max-height: calc(100vh - 21rem);
  }
}


.compact-results-card {
  position: absolute;
  top: -50px;
  left: 150px;
  width: 180px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 10px;
  z-index: 19;
}

.compact-results-wrapper {
  position: relative;
  min-width: 8rem;
  min-height: 3rem;
  background: white;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);

  body.body--dark & {
    background: $dark;
  }
}
</style>